import { supabase } from "@/integrations/supabase/client";

/**
 * THE reader for a user's tier, in the browser.
 *
 * ONE COLUMN. `user_credits_v2.tier` holds the tier. `profiles.plan`,
 * `profiles.tier`, `profiles.subscription_tier` and `user_enrichment_credits.tier`
 * all claimed to as well, and of 18 accounts only two had the first three
 * agreeing. Thirteen read "pro" here and "free" there, because handle_new_user
 * wrote 'pro' into profiles.tier at signup and nothing ever wrote it again. All
 * four are renamed `_retired_*_20260809` and this module is what replaced them.
 *
 * NO FALLBACK. The old code read `subscription_tier || tier || "free"`. A
 * fallback chain is a second source of truth and it is the one nobody updates:
 * a Stripe upgrade that writes one column leaves the chain answering from the
 * other. There is one read here and it has one source.
 *
 * SUPERADMIN IS NOT A TIER. It is the superadmin grant in public.user_roles.
 * Thirteen RLS policies gated admin access on `profiles.tier = 'superadmin'` — a
 * value no row has ever held — and were dead the whole time. Use `isAdmin()`.
 */

export type Tier = "free" | "trial" | "pro" | "agency" | "enterprise";

export const TIER_ORDER: Tier[] = ["free", "pro", "agency", "enterprise"];

/**
 * TRIAL IS A REAL TIER VALUE, AND IT RANKS AS PRO.
 *
 * Every signup from 2026-08-29 starts tier='trial' for five days. It is not a
 * flag beside the tier — it IS the tier, in the one column that holds the tier,
 * because a second place to look is the thing that goes stale (that is why
 * profiles.tier / .plan / .subscription_tier were retired on 2026-08-09).
 *
 * Ranking it at 2 means every `tierAtLeast(t, "pro")` gate admits a trial user
 * with no edit, and every `tier !== "free"` gate already did. What still needs
 * naming are the allow-list gates written as `["pro","agency",…].includes(t)` —
 * those are listed in the trial sweep and each one says whether it admits trial.
 *
 * It is NOT in TIER_ORDER: that array is the upgrade ladder shown on billing
 * surfaces, and a trial is not a rung somebody buys.
 */
export function tierRank(tier: string | null | undefined): number {
  switch (String(tier ?? "free").toLowerCase()) {
    case "enterprise":
      return 4;
    case "agency":
    case "agency_plus":
      return 3;
    case "pro":
    case "trial":
      return 2;
    default:
      return 1;
  }
}

/** Tiers that get Pro features. Trial is one of them, for five days. */
export const PRO_OR_BETTER = ["trial", "pro", "agency", "enterprise", "custom", "admin"];

/** Does this tier get Pro features right now? Trial does. */
export function isProOrBetter(tier: string | null | undefined): boolean {
  return PRO_OR_BETTER.includes(String(tier ?? "free").toLowerCase());
}

/** Is this account on the automatic 5-day trial? */
export function isTrial(tier: string | null | undefined): boolean {
  return String(tier ?? "").toLowerCase() === "trial";
}

/**
 * Whole days left on a trial, floor 0. `null` when there is no trial running.
 * Deliberately floors: telling somebody "1 day left" when it is 90 minutes is
 * the honest direction to round.
 */
export function trialDaysLeft(trialEndsAt: string | null | undefined): number | null {
  if (!trialEndsAt) return null;
  const ms = new Date(trialEndsAt).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function tierAtLeast(tier: string | null | undefined, min: Tier | string): boolean {
  return tierRank(tier) >= tierRank(min);
}

/** "pro" -> "Pro". For labels. */
export function tierLabel(tier: string | null | undefined): string {
  const t = String(tier ?? "free").toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * The signed-in user's own tier.
 *
 * A read failure returns "free" and logs. That is a deliberate direction: with
 * the gate closed the user sees an upgrade prompt, and with it open on an error
 * they would silently get a paid surface. It is logged loudly because a silent
 * downgrade is exactly the failure this whole change is about.
 */
export async function getMyTier(): Promise<Tier | string> {
  const { data, error } = await supabase.rpc("my_tier" as never);
  if (error) {
    console.error("[userTier] my_tier() failed; treating as free:", error.message);
    return "free";
  }
  return String(data ?? "free").toLowerCase();
}

/** Tiers for a set of users. Admin lists. Users the caller may not see are absent. */
export async function getTiersFor(userIds: string[]): Promise<Record<string, string>> {
  if (!userIds.length) return {};
  const { data, error } = await supabase.rpc("user_tiers" as never, { p_user_ids: userIds } as never);
  if (error) {
    console.error("[userTier] user_tiers() failed:", error.message);
    return {};
  }
  const out: Record<string, string> = {};
  for (const row of (data as unknown as { user_id: string; tier: string }[]) ?? []) {
    out[row.user_id] = String(row.tier ?? "free").toLowerCase();
  }
  return out;
}

/**
 * Admin flag. A separate fact from the tier, and never inferred from one.
 * Re-exported from src/lib/isAdmin.ts rather than reimplemented — two copies of
 * a predicate is how the tier ended up with five.
 */
export { isAdmin as isSuperadmin } from "@/lib/isAdmin";
